import * as Sentry from '@sentry/react';
import type { User } from 'features/users/types';
import { VideoPipDrawBranch, VideoPipRenderer } from 'utils/pip/video-pip/VideoPipRenderer';
import { logger } from 'utils/logger';
import { clearVideo } from 'utils/video';
import { PipEntryPoint } from 'utils/pip/types';
import { extractPipRequestState } from 'utils/pip/pipRequestDiagnostics';

export type VideoPipSettings = {
  showPlaceholder: boolean;
  active: boolean;
  muted: boolean;
};

export type VideoPipTelemetry = {
  prepared: boolean;
  drawBranch: VideoPipDrawBranch | null;
};

const REQUEST_STALL_TIMEOUT_MS = 10000;

const CAPTURE_FPS = 30;

// Draws beyond the capture rate are wasted GPU copies of the camera; 4 ms absorbs rAF jitter.
const MIN_FRAME_GAP_MS = 1000 / CAPTURE_FPS - 4;

export class VideoPipManagerClass {
  private renderer: VideoPipRenderer;

  private frameId: number | null = null;

  private lastDrawAt = -Infinity;

  private video: HTMLVideoElement | null = null;

  private stream: MediaStream | null = null;

  private active: boolean = false;

  private requesting: boolean = false;

  private prepared = false;

  private lastDrawBranch: VideoPipDrawBranch | null = null;

  constructor(renderer: VideoPipRenderer) {
    this.renderer = renderer;
  }

  async prepare(width: number, height: number, user: User, settings: VideoPipSettings) {
    if (this.active || this.video) {
      return;
    }

    const video = this.build(width, height, user, settings);

    video.playsInline = true;
    video.style.cssText =
      'position:fixed;bottom:0;left:0;width:1px;height:1px;opacity:0;pointer-events:none';

    document.body.appendChild(video);

    try {
      await video.play();
    } catch (error) {
      if (this.video !== video) {
        return;
      }

      logger.remote({ capture: 'pip' }).warn('[VideoPipManager] prepare failed:', error);
      this.stop();
      return;
    }

    if (this.video !== video || this.active) {
      return;
    }

    this.stopDrawing();
  }

  async requestWindow(
    width: number,
    height: number,
    user: User,
    settings: VideoPipSettings,
    entry: PipEntryPoint
  ): Promise<HTMLVideoElement | null> {
    if (this.requesting) {
      logger.remote({ capture: 'pip' }).warn('[VideoPipManager] PiP request already in flight');
      return null;
    }

    const preparedVideo = this.video;

    this.requesting = true;
    this.active = true;

    try {
      let video = preparedVideo;
      let playDurationMs = 0;

      if (video) {
        this.renderer.init({ user, settings });
        // Nothing may be awaited before requestPictureInPicture (Safari gesture), so paint now.
        this.paint();
      } else {
        video = this.build(width, height, user, settings);

        const playStartedAt = performance.now();
        await video.play();
        playDurationMs = Math.round(performance.now() - playStartedAt);
      }

      const diagnostics = extractPipRequestState(video, {
        entry,
        prepared: preparedVideo != null,
        playDurationMs,
      });

      logger.remote({ capture: 'pip' }).info('[VideoPipManager] requesting PiP', diagnostics);

      let stallTimeout = 0;

      if (preparedVideo) {
        stallTimeout = window.setTimeout(() => {
          logger
            .remote({ capture: 'pip' })
            .warn('[VideoPipManager] requestPictureInPicture has not settled', {
              afterMs: REQUEST_STALL_TIMEOUT_MS,
            });

          this.requesting = false;
          this.stop();
        }, REQUEST_STALL_TIMEOUT_MS);
      }

      try {
        await video.requestPictureInPicture();
      } catch (error) {
        logger
          .remote({ capture: 'pip' })
          .warn('[VideoPipManager] requestPictureInPicture failed:', error);
        Sentry.captureException(error, {
          tags: { 'pip.entry': entry },
          contexts: { pipRequest: diagnostics },
        });
        this.stop();
        return null;
      } finally {
        window.clearTimeout(stallTimeout);
      }

      // The stall timeout may have stopped the manager while the request was pending.
      if (preparedVideo && this.video === video) {
        this.startDrawing();
      }

      this.prepared = preparedVideo != null;

      return video;
    } finally {
      this.requesting = false;
    }
  }

  private build(width: number, height: number, user: User, settings: VideoPipSettings) {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const stream = canvas.captureStream(CAPTURE_FPS);
    const video = document.createElement('video');

    video.srcObject = stream;
    video.controls = false;
    video.muted = true;

    this.video = video;
    this.stream = stream;

    this.renderer.init({
      ctx: canvas.getContext('2d')!,
      user,
      settings,
    });
    // Safari's play() resolves only once the canvas stream has a frame.
    this.paint();

    video.addEventListener('play', () => {
      if (this.video === video) {
        this.startDrawing();
      }
    });

    return video;
  }

  private paint() {
    this.lastDrawBranch = this.renderer.draw();
  }

  private startDrawing() {
    if (this.frameId !== null) {
      return;
    }

    const tick = (now: DOMHighResTimeStamp) => {
      if (now - this.lastDrawAt >= MIN_FRAME_GAP_MS) {
        this.lastDrawAt = now;
        this.paint();
      }

      this.frameId = requestAnimationFrame(tick);
    };

    this.frameId = requestAnimationFrame(tick);
  }

  private stopDrawing() {
    if (this.frameId === null) {
      return;
    }

    cancelAnimationFrame(this.frameId);
    this.frameId = null;
  }

  redraw(user: User, settings: VideoPipSettings) {
    if (!this.isActive()) {
      return;
    }

    this.renderer.init({
      user,
      settings,
    });
  }

  stop() {
    this.active = false;

    if (this.video && document.pictureInPictureElement === this.video) {
      document.exitPictureInPicture();
    }

    this.stopDrawing();

    if (this.video) {
      clearVideo(this.video);
      this.video.remove();
    }

    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
    }

    this.video = null;
    this.stream = null;

    this.renderer.reset();
  }

  isActive() {
    return this.active;
  }

  // Survives stop(): the provider's unmount cleanup stops the element before reporting the close.
  telemetrySnapshot(): VideoPipTelemetry {
    return { prepared: this.prepared, drawBranch: this.lastDrawBranch };
  }
}

export const VideoPipManager = new VideoPipManagerClass(new VideoPipRenderer());
